import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import { FeatureTableOptions } from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentCoords } from 'types/geometric/geckoComponentCoords'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { parseString } from 'utils/parsingHelpers'
import {
  optionalStringSchema,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

import { GeckoPipeSupport } from './geckoPipeSupport'

export enum PipeNonIntegralSupportType {
  Clamp = 'clamp',
  Hanger = 'hanger',
  LimitStop = 'limit_stop',
  PipeGuide = 'pipe_guide',
  Special = 'special',
  Spring = 'spring',
  UBolt = 'u_bolt',
  Unknown = 'unknown',
}

export interface GeckoPipeNonIntegralSupportJSON {
  name: string | undefined
  type: GeckoFeatureModelType.PipeNonIntegralSupport
  pipe_support_type: PipeNonIntegralSupportType | undefined
  feature_geometry: {
    x: number
    y: number
  }
}

export const NewGeckoPipeNonIntegralSupportJSON: GeckoPipeNonIntegralSupportJSON =
  {
    type: GeckoFeatureModelType.PipeNonIntegralSupport,
    name: 'New Pipe Non-Integral Support',
    feature_geometry: {
      x: 0,
      y: 0,
    },
    pipe_support_type: undefined,
  }

export class GeckoPipeNonIntegralSupport extends GeckoPipeSupport {
  public readonly type = GeckoFeatureModelType.PipeNonIntegralSupport

  private constructor(
    name: string | undefined,
    pipeSupportID: string,
    pipeSupportType: PipeNonIntegralSupportType | undefined,
    geometry: GeckoComponentCoords
  ) {
    super(name, pipeSupportID, pipeSupportType, geometry)
  }

  public static CreateFromJSON(
    componentID: string,
    pipeSupportID: string,
    json: GeckoPipeNonIntegralSupportJSON
  ): GeckoPipeNonIntegralSupport {
    const name = parseString(json.name)

    const pipeSupportType =
      json.pipe_support_type === undefined
        ? undefined
        : (json.pipe_support_type as PipeNonIntegralSupportType)

    const featureGeometryData = json.feature_geometry as Record<string, number>
    const geometry = GeckoComponentCoords.CreateFromValues(
      componentID,
      featureGeometryData.x,
      GeckoMeasurementUnit.Meters,
      featureGeometryData.y,
      GeckoMeasurementUnit.Meters
    )

    return new GeckoPipeNonIntegralSupport(
      name,
      pipeSupportID,
      pipeSupportType,
      geometry
    )
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJSON: Record<string, GeckoPipeNonIntegralSupportJSON>
  ): Map<string, GeckoPipeNonIntegralSupport> {
    return Object.keys(recordsJSON).reduce((resultObj, id) => {
      resultObj.set(
        id,
        GeckoPipeNonIntegralSupport.CreateFromJSON(
          componentID,
          id,
          recordsJSON[id]
        )
      )
      return resultObj
    }, new Map<string, GeckoPipeNonIntegralSupport>())
  }

  public asJSON() {
    return generatePipeNonIntegralSupport(
      this.name,
      this.geometry.x,
      this.geometry.y,
      this.pipeSupportType as PipeNonIntegralSupportType
    )
  }
}

export function generatePipeNonIntegralSupport(
  name: string | undefined,
  x: GeckoMeasurement,
  y: GeckoMeasurement,
  pipeSupportType: PipeNonIntegralSupportType | undefined
): GeckoPipeNonIntegralSupportJSON {
  return {
    name: name,
    type: GeckoFeatureModelType.PipeNonIntegralSupport,
    feature_geometry: {
      x: x
        .convertTo(GeckoMeasurementUnit.Meters)
        .toPrecision(assetModelPrecision).num,
      y: y
        .convertTo(GeckoMeasurementUnit.Meters)
        .toPrecision(assetModelPrecision).num,
    },
    pipe_support_type: pipeSupportType,
  }
}

export const PipeNonIntegralSupportFormFields = (
  json: GeckoPipeNonIntegralSupportJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Name',
      value: json.name,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'x',
      label: 'X Position',
      value: json.feature_geometry.x,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y',
      label: 'Y Position',
      value: json.feature_geometry.y,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'pipe_support_type',
      label: 'Pipe Support Type',
      value: json.pipe_support_type,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      options: [
        {
          value: PipeNonIntegralSupportType.Unknown,
          label: 'Unknown',
        },
        {
          value: PipeNonIntegralSupportType.Clamp,
          label: 'Clamp',
        },
        {
          value: PipeNonIntegralSupportType.Hanger,
          label: 'Hanger',
        },
        {
          value: PipeNonIntegralSupportType.LimitStop,
          label: 'Limit Stop',
        },
        {
          value: PipeNonIntegralSupportType.PipeGuide,
          label: 'Pipe Guide',
        },
        {
          value: PipeNonIntegralSupportType.Special,
          label: 'Special',
        },
        {
          value: PipeNonIntegralSupportType.Spring,
          label: 'Spring',
        },
        {
          value: PipeNonIntegralSupportType.UBolt,
          label: 'U-Bolt',
        },
      ],
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Dropdown,
    },
  ]
}

export const PipeNonIntegralSupportJSONFromFormFields = (
  formFields: FormField[]
) => {
  return generatePipeNonIntegralSupport(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'x')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    getFormFieldByDataKey(formFields, 'pipe_support_type')
      ?.value as PipeNonIntegralSupportType
  )
}

export const PipeNonIntegralSupportFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.PipeNonIntegralSupport,
  title: 'Pipe Non-Integral Supports',
  singularName: 'Pipe Non-Integral Support',
  fields: ['x', 'y', 'pipe_support_type'],
}
